<template>
  <v-card elevation="2" class="data-table-container rounded-lg overflow-hidden">
    <!-- Top Actions Header -->
    <div class="table-header-actions pa-4 pb-0">
      <div class="d-flex flex-wrap align-center justify-space-between mb-4">
        <div class="action-buttons-group d-flex flex-nowrap overflow-x-auto pb-2 mr-2">
          <template v-for="(button, index) in config.buttons">
            <v-btn
              v-show="button.show !== false"
              :key="index"
              @click="
                onClickEvent(
                  button,
                  button.require ? button.require : false,
                  button.limitRequire ? button.limitRequire : 0
                )
              "
              :loading="loadingButtons[button.buttonId]"
              class="ma-1 action-btn transition-swing"
              rounded
              depressed
              :color="button.color || $pgsHelper.uiConfig().mainColor"
              dark
            >
              <v-icon left size="18" v-if="button.icon && button.icon.includes('mdi-')">{{ button.icon }}</v-icon>
              <i v-else-if="button.icon" :class="[button.icon, 'mr-2']"></i>
              {{ button.text }}
            </v-btn>
          </template>
        </div>

        <div v-if="config.allowExport" class="export-container">
          <v-btn color="success" depressed rounded @click="exportExcel" :loading="exporting" :disabled="exporting" class="export-btn font-weight-bold">
            <v-icon left>mdi-file-excel-box</v-icon>
            Exportar
          </v-btn>
        </div>
      </div>

      <v-row dense>
        <v-col cols="12" md="12">
          <v-text-field v-model="filter" prepend-inner-icon="mdi-magnify" label="Buscar en la tabla..."
            class="search-field mb-4" filled rounded dense hide-details clearable></v-text-field>
        </v-col>
      </v-row>
    </div>

    <!-- Data Table -->
    <v-data-table :sort-by.sync="config.orderByCol.Field" v-model="selectedRow" :headers="config.fields"
      :single-select="config.selectableRow" :item-key="config.selectConfig.idRowData" show-select :items="filtereddata"
      :search="filter" :no-data-text="'No se encontraron registros activos'" @click:row="rowClick" light
      loading-text="Cargando información..." :footer-props="{
        showFirstLastPage: true,
        'items-per-page-text': 'Filas por página',
      }" :hide-default-footer="config.disablePagination" :disable-pagination="config.disablePagination"
      :items-per-page="pageRows ? pageRows : 15" class="modern-data-table">
      <!-- Custom Header Slots -->
      <template v-for="(header, i) in config.fields" v-slot:[`header.${header.value}`]="{ }">
        <div class="d-flex align-center custom-header" :key="i">
          <span class="text-subtitle-2 font-weight-bold text-uppercase">{{ header.text }}</span>
          <v-menu v-model="tooltips[i]" :close-on-content-click="false" offset-y
            v-if="header.canBeFiltered && !header.buttons" transition="scale-transition" @input="val => onMenuOpen(val, header.value)">
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon v-bind="attrs" v-on="on" small class="ml-1 filter-icon">
                <v-icon size="16"
                  :color="(multiSearch[header.value] && multiSearch[header.value].length) || (dateFilters[header.value] && (dateFilters[header.value].start || dateFilters[header.value].end)) ? 'primary' : 'grey'">
                  mdi-filter-variant
                </v-icon>
              </v-btn>
            </template>
            <v-card min-width="250" class="rounded-lg">
              <v-card-title class="text-subtitle-1 py-2 secondary white--text">
                Filtrar por {{ header.text }}
              </v-card-title>
              <v-card-text class="pt-4">
                <template v-if="isDateField(header)">
                  <v-row dense>
                    <v-col cols="12" sm="6">
                      <v-menu v-model="dateMenus[header.value].start" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field v-model="dateFilters[header.value].start" label="Desde" prepend-icon="mdi-calendar" readonly v-bind="attrs" v-on="on" dense outlined hide-details></v-text-field>
                        </template>
                        <v-date-picker v-model="dateFilters[header.value].start" :type="isMonthOnlyField(header) ? 'month' : 'date'" @input="dateMenus[header.value].start = false" no-title scrollable></v-date-picker>
                      </v-menu>
                    </v-col>
                    <v-col cols="12" sm="6">
                      <v-menu v-model="dateMenus[header.value].end" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field v-model="dateFilters[header.value].end" label="Hasta" prepend-icon="mdi-calendar" readonly v-bind="attrs" v-on="on" dense outlined hide-details></v-text-field>
                        </template>
                        <v-date-picker v-model="dateFilters[header.value].end" :type="isMonthOnlyField(header) ? 'month' : 'date'" @input="dateMenus[header.value].end = false" no-title scrollable></v-date-picker>
                      </v-menu>
                    </v-col>
                    <v-col cols="12" class="pt-2 d-flex justify-end">
                      <v-btn small color="primary" class="mr-2" @click="applyDateFilter(header.value)">Aplicar</v-btn>
                      <v-btn small text @click="clearDateFilter(header.value)">Limpiar</v-btn>
                    </v-col>
                  </v-row>
                </template>
                <template v-else>
                  <v-combobox v-model="multiSearch[header.value]" :items="getUniqueValues(header.value)"
                    label="Seleccione valores" clearable multiple chips small-chips outlined hide-details></v-combobox>
                </template>
              </v-card-text>
            </v-card>
          </v-menu>
        </div>
      </template>

      <!-- Custom Item Slots -->
      <template v-for="(field, index) in formatFields" v-slot:[`item.${field.value}`]="{ item }">
        <!-- Format based content -->
        <div v-if="!field.buttons" :key="index" class="formatted-cell" v-html="field.format(item)"></div>

        <!-- Action Buttons -->
        <div v-if="field.buttons" :key="index + 'btn'" class="d-flex align-center py-1">
          <template v-for="(btn, btnindex) in field.buttons">
            <v-tooltip v-if="btn.tooltip" :key="`tooltip-${btnindex}`" bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn v-bind="attrs" v-on="on" elevation="0" x-small :color="btn.color" class="mx-1" :loading="btn.doLoading ? loadingButtons[btn.buttonId + '-' + item.id] : loadingButtons[btn.buttonId]"
                  v-show="btn.hasOwnProperty('showBy') ? item[btn.showBy] : true"
                  @click.stop="onClickEvent(btn, false, 0, item[btn.value], btn.doLoading ? item.id : null)">
                  <v-icon v-if="btn.icon && btn.icon.includes('mdi-')" size="18">{{ btn.icon }}</v-icon>
                  <i v-else-if="btn.icon" :class="[btn.icon]"></i>
                  <span v-else>{{ btn.text }}</span>
                </v-btn>
              </template>
              <span>{{ btn.tooltip }}</span>
            </v-tooltip>

            <v-btn v-else v-show="btn.hasOwnProperty('showBy') ? item[btn.showBy] : true" :key="`button-${btnindex}`"
              :color="btn.color" elevation="0" x-small class="mx-1" :loading="btn.doLoading ? loadingButtons[btn.buttonId + '-' + item.id] : loadingButtons[btn.buttonId]"
              @click.stop="onClickEvent(btn, false, 0, item[btn.value], btn.doLoading ? item.id : null)">
              <v-icon v-if="btn.icon && btn.icon.includes('mdi-')" size="18">{{ btn.icon }}</v-icon>
              <i v-else-if="btn.icon" :class="[btn.icon]"></i>
              <span v-else>{{ btn.text }}</span>
            </v-btn>
          </template>
        </div>
      </template>
    </v-data-table>
  </v-card>
</template>

<script>
import DataTableExporter from "../../clases/ExcelMaker/DataTableExcel";
import moment from "moment";

export default {
  name: "DataTable",
  components: {},
  props: ["data", "config", "pageRows"],
  data() {
    return {
      notData: false,
      filter: "",
      selectedPage: 1,
      selectedRow: [],
      selectedRows: [],
      filteredData: [],

      multiSearch: {},
      tooltips: [],
      dateFilters: {},
      dateMenus: {},
      loadingButtons: {},
      exporting: false,
    };
  },
  created() {
    // Initialize filters for current config (if present)
    this.initializeFilters(this.config && this.config.fields);
  },
  computed: {
    formatFields() {
      let fields = [];
      this.config.fields.forEach((field) => {
        if (field.format || field.buttons) {
          fields.push(field);
        }
      });
      return fields;
    },

    // filtereddata() {
    //   return this.data.filter((item) => {
    //     // Recorre todos los filtros aplicados en multiSearch
    //     return Object.entries(this.multiSearch).every(([key, values]) => {
    //       if (!values || values.length === 0) return true; // Sin filtro, acepta el registro

    //       // Encuentra el campo correspondiente en config
    //       const field = this.config.fields.find((f) => f.value === key);
    //       const searchableValue = this.getFormattedValue(item, field); // Obtén el valor procesado

    //       // Compara el valor con los filtros aplicados
    //       return values.some((v) =>
    //         searchableValue
    //           .toString()
    //           .trim()
    //           .toUpperCase()
    //           .includes(
    //             v
    //               .toString()
    //               .trim()
    //               .toUpperCase()
    //           )
    //       );
    //     });
    //   });
    // },
    filtereddata() {
      return this.data.filter((item) => {
        // Primero aplicar filtros de múltiples valores (combobox)
        const multiPass = Object.entries(this.multiSearch).every(([key, values]) => {
          if (!values || values.length === 0) return true;

          const field = this.config.fields.find((f) => f.value === key);
          if (!field) return true;

          const searchableValue = this.getFormattedValue(item, field) || "";
          const itemValues = searchableValue
            .toString()
            .split(",")
            .map((v) => v.trim().toUpperCase());
          return values.some((value) =>
            itemValues.includes(value.toString().trim().toUpperCase())
          );
        });

        if (!multiPass) return false;

        // Luego aplicar filtros por rango de fecha
        for (const [key, range] of Object.entries(this.dateFilters || {})) {
          if (!range) continue;
          const hasStart = range.start && range.start.toString().length > 0;
          const hasEnd = range.end && range.end.toString().length > 0;
          if (!hasStart && !hasEnd) continue;

          const field = this.config.fields.find((f) => f.value === key);
          if (!field) continue;
          const monthOnly = this.isMonthOnlyField(field);

          const rawValue = this.getFormattedValue(item, field);
          const itemDate = this.parseDate(rawValue);
          if (!itemDate) return false;

          if (monthOnly) {
            const itemMonth = itemDate.getMonth();
            const startMonth = hasStart ? this.getMonthIndex(range.start) : null;
            const endMonth = hasEnd ? this.getMonthIndex(range.end) : null;

            if (hasStart && startMonth === null) return false;
            if (hasEnd && endMonth === null) return false;

            if (hasStart && hasEnd) {
              if (startMonth <= endMonth) {
                if (itemMonth < startMonth || itemMonth > endMonth) return false;
              } else {
                // Wrap-around range (e.g., Nov -> Feb) when year is ignored.
                if (itemMonth < startMonth && itemMonth > endMonth) return false;
              }
            } else if (hasStart) {
              if (itemMonth < startMonth) return false;
            } else if (hasEnd) {
              if (itemMonth > endMonth) return false;
            }

            continue;
          }

          if (hasStart) {
            const startDate = this.parseDate(range.start);
            if (startDate && itemDate < startDate) return false;
          }
          if (hasEnd) {
            const endDate = this.parseDate(range.end);
            if (endDate) {
              endDate.setHours(23, 59, 59, 999);
              if (itemDate > endDate) return false;
            }
          }
        }

        return true;
      });
    },
  },
  watch: {
    config: {
      handler(newConfig) {
        if (newConfig && Array.isArray(newConfig.fields)) {
          this.initializeFilters(newConfig.fields);
        }
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    initializeFilters(fields) {
      if (!fields || !Array.isArray(fields)) return;
      fields.forEach((f) => {
        if (f && f.value) {
          if (f.canBeFiltered && !this.dateFilters[f.value]) {
            this.$set(this.dateFilters, f.value, { start: null, end: null });
          }
          if (f.canBeFiltered && !this.dateMenus[f.value]) {
            this.$set(this.dateMenus, f.value, { start: false, end: false });
          }
          if (!this.multiSearch[f.value]) {
            this.$set(this.multiSearch, f.value, []);
          }
        }
      });
    },
    exportExcel() {
      if (this.exporting) return;
      this.exporting = true;
      DataTableExporter.generateExcelBuffer(this.filtereddata, this.config)
        .then((buffer) => {
          const blob = new Blob([buffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = `${this.config.rawText}.xlsx`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        })
        .catch((err) => {
          console.error("Error exporting to Excel:", err);
        })
        .finally(() => {
          this.exporting = false;
        });
    },

    getFormattedValue(item, field) {
      if (!field || !field.value) return "";
      const value = item[field.value];
      if (field.format) {
        // Usa el formato definido en el campo si existe y elimina etiquetas HTML
        try {
          return this.stripHtmlTags(field.format(item));
        } catch (e) {
          return "";
        }
      }
      if (Array.isArray(value)) {
        // Convierte arrays en una lista legible
        return value
          .map((v) => (typeof v === "object" ? JSON.stringify(v) : v))
          .join(", ");
      }
      if (typeof value === "object" && value !== null) {
        // Convierte objetos a una representación legible
        return JSON.stringify(value);
      }
      return value !== null && value !== undefined ? value.toString() : ""; // Maneja valores simples
    },
    stripHtmlTags(html) {
      // los formatos repiten muy pocos valores distintos: se parsea cada uno una sola vez
      if (!this.stripCache) this.stripCache = new Map();
      if (this.stripCache.has(html)) return this.stripCache.get(html);
      const doc = new DOMParser().parseFromString(html, "text/html");
      const text = doc.body.textContent || "";
      if (this.stripCache.size < 5000) this.stripCache.set(html, text);
      return text;
    },
    isDateField(header) {
      if (!header || !header.canBeFiltered) return false;
      if (header.dateFilterMode === "month" || header.dateFilterMode === "all") {
        return true;
      }

      const typeHint = String(header.type || "").toLowerCase();
      return ["date", "datetime", "timestamp"].includes(typeHint);
    },
    isMonthOnlyField(header) {
      return !!(header && header.dateFilterMode === "month");
    },
    onMenuOpen(open, key) {
      if (!open) return;
      const field = this.config.fields.find((f) => f.value === key);
      if (field && this.isDateField(field) && !this.dateFilters[key]) {
        this.$set(this.dateFilters, key, { start: null, end: null });
      }
    },

    applyDateFilter(key) {
      const idx = this.config.fields.findIndex((f) => f.value === key);
      if (idx >= 0) {
        this.$set(this.tooltips, idx, false);
      }
    },

    clearDateFilter(key) {
      this.$set(this.dateFilters, key, { start: null, end: null });
    },

    parseDate(value) {
      if (!value) return null;
      if (value instanceof Date) return value;
      if (typeof value === "number") return new Date(value);

      const formats = [
        "DD-MM-YYYY",
        "DD/MM/YYYY",
        "YYYY-MM-DD",
        "YYYY/MM/DD",
        "YYYY-MM",
        moment.ISO_8601,
      ];

      const parsed = moment(String(value).trim(), formats);

      return parsed.isValid() ? parsed.toDate() : null;
    },
    getMonthIndex(value) {
      const date = this.parseDate(value);
      return date ? date.getMonth() : null;
    },
    // getUniqueValues(key) {
    //   const field = this.config.fields.find((f) => f.value === key);

    //   return [
    //     ...new Set(
    //       this.data.map((item) => {
    //         let value;

    //         if (field && field.format) {
    //           // Procesa el valor formateado y elimina etiquetas HTML
    //           const formattedValue = field.format(item);
    //           value = this.stripHtmlTags(formattedValue);
    //         } else {
    //           // Usa el valor crudo si no hay formato
    //           value = item[key] || "";
    //         }

    //         // Normaliza el valor para evitar duplicados inconsistentes
    //         return value
    //           .toString()
    //           .trim()
    //           .toUpperCase();
    //       })
    //     ),
    //   ];
    // },
    getUniqueValues(key) {
      const field = this.config.fields.find((f) => f.value === key);

      // Extraer y normalizar valores únicos
      return [
        ...new Set(
          this.data.flatMap((item) => {
            let value;

            if (field && field.format) {
              // Procesa el valor formateado y elimina etiquetas HTML
              const formattedValue = field.format(item);
              value = this.stripHtmlTags(formattedValue);
            } else {
              // Usa el valor crudo si no hay formato
              value = item[key] || "";
            }

            // Divide valores múltiples por el delimitador (coma en este caso) y normaliza
            return value
              .toString()
              .split(",")
              .map((v) => v.trim().toUpperCase()); // Normaliza cada valor
          })
        ),
      ];
    },
    retrieveData() {
      return this.filtereddata;
    },
    getFiltered(e) {
      let fields = [];
      e.forEach((item) => {
        let data = {};
        this.config.fields.forEach((campo) => {
          if (campo.format && !campo.buttons) {
            data[campo.text] = campo.format(item);
          }
          if (!campo.format && !campo.buttons) {
            data[campo.text] = item[campo.value];
          }
        });
        fields.push(data);
      });

      this.filteredData = fields;
    },
    rowClick: function (item, row) {
      this.selectedRow = []
      row.select(true);
      this.selectedRow = [item]
      //item  - selected item
    },
    onClickEvent(button, require, limit, customValue = null, itemId = null) {
      const buttonId = typeof button === 'string' ? button : (button.buttonId + (itemId ? '-' + itemId : ''));
      const doLoading = typeof button !== 'string' && button.doLoading;
      
      if (require && this.selectedRow.length <= 0) {
        this.$swal(
          "Seleccione un registro para continuar con la acción",
          "Antes de continuar selecciona un registro haciendo click sobre el en la tabla",
          "error"
        );
        return false;
      }
      if (limit != 0) {
        if (this.selectedRow.length > limit) {
          this.$swal(
            "Esta opcion solo admite " + limit + " registro a la vez",
            "Antes de continuar selecciona un registro haciendo click sobre el en la tabla",
            "error"
          );
          return false;
        }
      }
      
      if (doLoading) this.$set(this.loadingButtons, buttonId, true);
      
      let selectedKeys = [];
      this.selectedRow.forEach((select) => {
        selectedKeys.push(select[this.config.selectConfig.idRowData]);
      });
      this.$emit(
        "buttonClick",
        buttonId,
        customValue ? customValue : selectedKeys
      );
      
      // this.selectedRow = [];
    },
    stopLoading(buttonId) {
      // ensure reactive update
      try {
        this.$set(this.loadingButtons, buttonId, false);
      } catch (e) {
        this.loadingButtons[buttonId] = false;
      }
    },
  },
  beforeMount: function () {
    this.loading = false;
  },
  
  mounted: function () {
    this.loading = false;
  },
};
</script>

<style scoped>
.data-table-container {
  background-color: #fcfcfc !important;
  border: 1px solid rgba(0, 0, 0, 0.05);
}

.table-header-actions {
  background: white;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.action-buttons-group {
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.2) transparent;
}

.action-buttons-group::-webkit-scrollbar {
  height: 4px;
}

.action-buttons-group::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.1);
  border-radius: 10px;
}

.action-btn {
  text-transform: none;
  font-weight: 600;
  letter-spacing: 0.3px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06) !important;
}

.action-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05) !important;
}

.export-btn {
  text-transform: none;
  letter-spacing: 0.5px;
}

.search-field /deep/ .v-input__slot {
  background: #f5f7fa !important;
  border: 1px solid transparent;
  transition: all 0.3s ease;
}

.search-field /deep/ .v-input__slot:focus-within {
  background: white !important;
  border-color: var(--v-primary-base);
  box-shadow: 0 0 0 4px rgba(33, 150, 243, 0.1);
}

.modern-data-table {
  background: transparent !important;
}

.modern-data-table /deep/ thead th {
  background-color: #f8fafc !important;
  height: 56px !important;
  color: #64748b !important;
  border-bottom: 2px solid #e2e8f0 !important;
}

.modern-data-table /deep/ tbody tr {
  transition: background-color 0.2s ease;
}

.modern-data-table /deep/ tbody tr:hover {
  background-color: #f1f5f9 !important;
}

.modern-data-table /deep/ tr.v-data-table__selected {
  background: rgba(33, 133, 208, 0.1) !important;
  position: relative;
}

.modern-data-table /deep/ tr.v-data-table__selected::after {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  background-color: #2185d0;
}

.modern-data-table /deep/ .v-data-table__wrapper {
  overflow-x: auto;
}

.custom-header {
  white-space: nowrap;
}

.filter-icon:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.formatted-cell {
  font-size: 0.875rem;
  color: #334155;
  line-height: 1.5;
}

/* Footer styling */
.modern-data-table /deep/ .v-data-footer {
  border-top: 1px solid #e2e8f0;
  padding: 8px 16px;
  background: #f8fafc;
}
</style>
